import { ArgumentError } from '@plugin/Common/exceptions/cross-sell/argument.error'
import { HttpError } from '@plugin/Common/exceptions/cross-sell/http.error'
import { HttpClientInterface, HttpRequestOptions } from '@plugin/Common/interfaces/cross-sell/api/http-client.interface'
import { HttpMethodType } from '@plugin/Common/types/cross-sell/http-method.type'

abstract class HttpClientService implements HttpClientInterface {
  protected readonly apiBaseUrl

  constructor(apiBaseUrl: string) {
    if (!apiBaseUrl || !apiBaseUrl.trim()) {
      throw new ArgumentError('`apiBaseUrl` is required to configure HttpClientService')
    }
    this.apiBaseUrl = apiBaseUrl
  }

  public async delete<T>(url: string, options: HttpRequestOptions = {}): Promise<T> {
    try {
      const res = await fetch(this.generateApiUrl(url), {
        ...HttpClientService.convertRequestOptions(options),
        method: HttpMethodType.DELETE,
      })
      return HttpClientService.handleResponse<T>(res)
    } catch (err) {
      // TODO: Address this tech debt by adding proper type checking
      // @ts-expect-error Untyped error handling
      return HttpClientService.handleError(err)
    }
  }

  public async get<T>(url: string, options: HttpRequestOptions = {}): Promise<T> {
    try {
      const res = await fetch(this.generateApiUrl(url), {
        ...HttpClientService.convertRequestOptions(options),
        method: HttpMethodType.GET,
      })
      return HttpClientService.handleResponse<T>(res)
    } catch (err) {
      // TODO: Address this tech debt by adding proper type checking
      // @ts-expect-error Untyped error handling
      return HttpClientService.handleError(err)
    }
  }

  public async post<T>(url: string, options: HttpRequestOptions = {}): Promise<T> {
    try {
      const res = await fetch(this.generateApiUrl(url), {
        ...HttpClientService.convertRequestOptions(options),
        headers: {
          ...(options.headers || {}),
          'Content-Type': 'application/json',
        },
        method: HttpMethodType.POST,
      })
      return HttpClientService.handleResponse<T>(res)
    } catch (err) {
      // TODO: Address this tech debt by adding proper type checking
      // @ts-expect-error Untyped error handling
      return HttpClientService.handleError(err)
    }
  }

  public async put<T>(url: string, options: HttpRequestOptions = {}): Promise<T> {
    try {
      const res = await fetch(this.generateApiUrl(url), {
        ...HttpClientService.convertRequestOptions(options),
        headers: {
          ...(options.headers || {}),
          'Content-Type': 'application/json',
        },
        method: HttpMethodType.PUT,
      })
      return HttpClientService.handleResponse<T>(res)
    } catch (err) {
      // TODO: Address this tech debt by adding proper type checking
      // @ts-expect-error Untyped error handling
      return HttpClientService.handleError(err)
    }
  }

  protected generateApiUrl(url: string): string {
    return `${this.apiBaseUrl}/${url}`
  }

  protected static convertRequestOptions(options: HttpRequestOptions): RequestInit {
    return {
      body: JSON.stringify(options.body),
      headers: options.headers,
    }
  }

  protected static handleResponse<T>(res: Response): T | PromiseLike<T> {
    if (!res.ok) {
      throw new HttpError(res.statusText)
    }
    const response = res.status === 204 ? null : res.json()
    return response as Promise<T>
  }

  protected static handleError(err: Error): PromiseLike<never> {
    throw new HttpError(err.message)
  }
}

export { HttpClientService }
